import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const ITEMS_PER_PAGE = 6;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get('products');
        setProducts(res.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        setMessage('Error fetching products');
      }
    };
    fetchProducts();
  }, []);

  const handleQuantityChange = (productId, value) => {
    setQuantities((prev) => ({ ...prev, [productId]: value }));
  };

  const addToCart = async (product) => {
    const quantity = parseInt(quantities[product._id] || 1, 10);
    if (quantity <= 0) {
      setMessage('Quantity must be greater than 0');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.post(
        'cart/add',
        { productId: product._id, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Product added to cart!');
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      console.error('Error adding product to cart:', error);
      setMessage(error.response?.data?.message || 'Error adding product to cart');
    }
  };

  const filteredProducts = products.filter((product) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(lowerSearch) ||
      product.description.toLowerCase().includes(lowerSearch) ||
      product.category.toLowerCase().includes(lowerSearch)
    );
  });

  let sortedProducts = [...filteredProducts];
  switch (sortOption) {
    case 'price-asc':
      sortedProducts.sort((a, b) => a.price - b.price);
      break;
    case 'price-desc':
      sortedProducts.sort((a, b) => b.price - a.price);
      break;
    case 'name-asc':
      sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'name-desc':
      sortedProducts.sort((a, b) => b.name.localeCompare(a.name));
      break;
    default:
      break;
  }

  const totalProducts = sortedProducts.length;
  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

  if (currentPage > totalPages && totalPages !== 0) {
    setCurrentPage(1);
  }

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentProducts = sortedProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-success fw-bold">Our Products</h2>

      {message && (
        <div className="alert alert-primary text-center" role="alert">
          {message}
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center mb-4" style={{ gap: '0.5rem' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-control w-auto"
        />
        <select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          className="form-select w-auto"
        >
          <option value="">No sort</option>
          <option value="price-asc">Price (Low → High)</option>
          <option value="price-desc">Price (High → Low)</option>
          <option value="name-asc">Name (A → Z)</option>
          <option value="name-desc">Name (Z → A)</option>
        </select>
      </div>

      <div className="row">
        {currentProducts.map((product) => (
          <div key={product._id} className="col-md-4 mb-4">
            <div className="card h-100 border-0 shadow">
              <Link to={`/product/${product._id}`} className="text-decoration-none">
                <img
                  src={
                    product.images?.[0]
                      ? `https://api.iovendosiciliano.it/${product.images[0]}`
                      : 'https://via.placeholder.com/150'
                  }
                  className="card-img-top"
                  alt={product.name}
                  style={{ objectFit: 'cover', height: '220px' }}
                />
              </Link>
              <div className="card-body d-flex flex-column">
                <div className="flex-grow-1">
                  <h5 className="card-title text-success">
                    <Link to={`/product/${product._id}`} className="text-success text-decoration-none">
                      {product.name}
                    </Link>
                  </h5>
                  <p className="card-text text-truncate-multiline">{product.description}</p>
                  <p className="card-text mb-1"><strong>Price:</strong> €{product.price}</p>
                  <p className="card-text mb-1"><strong>Category:</strong> {product.category}</p>
                  <p className="card-text mb-2"><strong>Stock:</strong> {product.stock}</p>
                  <div className="mb-3">
                    <label
                      htmlFor={`quantity-${product._id}`}
                      className="form-label fw-semibold"
                      style={{ fontSize: '0.9rem' }}
                    >
                      Quantity
                    </label>
                    <input
                      id={`quantity-${product._id}`}
                      type="number"
                      min="1"
                      className="form-control"
                      style={{ width: '80px' }}
                      value={quantities[product._id] || 1}
                      onChange={(e) => handleQuantityChange(product._id, e.target.value)}
                    />
                  </div>
                </div>
                <button
                  className="btn btn-success mt-auto w-100"
                  onClick={() => addToCart(product)}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        ))}

        {currentProducts.length === 0 && (
          <div className="col-12">
            <div className="alert alert-info">Nessun prodotto trovato.</div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-4" style={{ gap: '1rem' }}>
          <button className="btn btn-outline-success" onClick={goToPrevPage} disabled={currentPage === 1}>
            ← Prev
          </button>
          <span>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button className="btn btn-outline-success" onClick={goToNextPage} disabled={currentPage === totalPages}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default Products;
